import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default banners if none exist
const DEFAULT_BANNERS = [
  {
    id: 'default-1',
    title: 'Temukan Produk Terbaik',
    image_url: 'https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=1200&h=400&fit=crop',
    link_url: null
  },
  {
    id: 'default-2',
    title: 'Belanja Sembako Mudah',
    image_url: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=1200&h=400&fit=crop',
    link_url: null
  },
  {
    id: 'default-3',
    title: 'Promo Spesial Hari Ini',
    image_url: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1200&h=400&fit=crop',
    link_url: null
  }
];

export const BannerCarousel = () => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await axios.get(`${API}/banners`);
        if (response.data.banners && response.data.banners.length > 0) {
          setBanners(response.data.banners);
        } else {
          setBanners(DEFAULT_BANNERS);
        }
      } catch (error) {
        console.error('Error fetching banners:', error);
        setBanners(DEFAULT_BANNERS);
      }
    };
    fetchBanners();
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % banners.length);
  }, [banners.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
  }, [banners.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying || banners.length <= 1) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide, banners.length]);

  const handleBannerClick = (banner) => {
    if (banner.link_url) {
      window.open(banner.link_url, '_blank');
    }
  };

  if (banners.length === 0) {
    return (
      <div className="relative w-full h-[200px] md:h-[300px] lg:h-[400px] bg-gradient-to-r from-[#0052CC] to-[#0066FF] animate-pulse" />
    );
  }

  return (
    <div
      className="relative w-full overflow-hidden"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
      data-testid="banner-carousel"
    >
      {/* Banner Container */}
      <div
        className="flex transition-transform duration-500 ease-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {banners.map((banner, index) => (
          <div
            key={banner.id}
            className="relative w-full flex-shrink-0 h-[200px] md:h-[300px] lg:h-[400px] cursor-pointer"
            onClick={() => handleBannerClick(banner)}
          >
            <img
              src={banner.image_url}
              alt={banner.title}
              className="w-full h-full object-cover"
              loading={index === 0 ? 'eager' : 'lazy'}
            />
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />

            {/* Banner Title */}
            <div className="absolute inset-0 flex items-center">
              <div className="max-w-7xl mx-auto px-4 md:px-8 w-full">
                {/* ✅ Only show title if it exists */}
                {banner.title && (
                  <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white max-w-xl leading-tight drop-shadow-lg">
                    {banner.title}
                  </h2>
                )}
                {banner.link_url && (
                  <button className="mt-4 bg-[#FF9900] hover:bg-[#E68A00] text-white px-6 py-2 rounded-full font-semibold transition-colors">
                    Lihat Selengkapnya
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      {banners.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prevSlide(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
            aria-label="Previous slide"
            data-testid="banner-prev"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-white" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextSlide(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
            aria-label="Next slide"
            data-testid="banner-next"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-white" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {banners.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`
                w-2.5 h-2.5 rounded-full transition-all duration-300
                ${currentIndex === index
                  ? 'bg-white w-8'
                  : 'bg-white/50 hover:bg-white/75'
                }
              `}
              aria-label={`Go to slide ${index + 1}`}
              data-testid={`banner-dot-${index}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerCarousel;
